<template>
  <div>
    <Navbar activeDirectory="admin" activePage="admin-modules"></Navbar>
    <div class="container-fluid py-4">

      <!-- ===== PAGE HEADER ===== -->
      <div class="d-flex align-items-center justify-content-between flex-wrap mb-4" style="gap:12px;">
        <div class="d-flex align-items-center">
          <div class="cw-header-icon mr-3">
            <i class="mdi mdi-view-dashboard"></i>
          </div>
          <div class="text-left">
            <h3 class="mb-0 font-weight-bold text-dark">Moduli e asset associati</h3>
            <p class="mb-0 text-muted small">Visualizza gli asset su mappa e gestisci l'attivazione delle tipologie.</p>
          </div>
        </div>
      </div>

      <!-- MAIN TABS (2 Sezioni Pulite: Asset Fisici & Configurazione) -->
      <div class="cw-location-tabs mb-4" style="border-bottom: 2px solid #e2e8f0; padding-bottom: 0;">
        <button type="button" class="cw-loc-tab" :class="{ active: activeTab === 'assets' }" @click="activeTab = 'assets'">
          <i class="mdi mdi-map-marker-radius mb-1"></i><span>Asset Fisici & Mappa</span>
        </button>
        <button type="button" class="cw-loc-tab" :class="{ active: activeTab === 'config' }" @click="activeTab = 'config'">
          <i class="mdi mdi-cog-outline mb-1"></i><span>Configurazione Moduli</span>
        </button>
      </div>

      <!-- TAB 1: CONFIGURAZIONE MODULI -->
      <div v-if="activeTab === 'config'">

      <!-- CARICAMENTO INIZIALE -->
      <div v-if="loadingInit" class="text-center py-5">
        <span class="spinner-border spinner-border-sm text-primary mr-2" role="status"></span>
        <span class="text-muted">Caricamento moduli e configurazioni in corso...</span>
      </div>

      <!-- NESSUN MODULO -->
      <div v-else-if="modules.length === 0" class="cw-card text-center py-5">
        <i class="mdi mdi-cube-off-outline" style="font-size:3rem; color:#cbd5e1;"></i>
        <h5 class="mt-3 text-dark">Nessun Modulo Attivo</h5>
        <p class="text-muted small">La tua azienda non ha alcun modulo assegnato.</p>
      </div>

      <div v-else>
        <!-- BANNER INFO -->
        <div class="au-banner-info">
          <i class="mdi mdi-domain au-banner-icon"></i>
          <div class="text-left">
            <strong class="au-banner-title">Gestione Tipologie e Interventi</strong>
            <span class="au-banner-text">
              Le tipologie elencate qui sono configurate per il tuo modulo. Puoi disabilitare quelle non necessarie alla tua azienda per semplificare le interfacce operative.
            </span>
          </div>
        </div>

        <!-- TABS DEI MODULI (Mostrato solo se ci sono più moduli) -->
        <div v-if="modules.length > 1" class="mb-4">
          <label class="font-weight-bold text-muted small text-uppercase mb-2 d-block">Seleziona il modulo da configurare:</label>
          <div class="cw-location-tabs">
            <button type="button" class="cw-loc-tab" v-for="mod in modules" :key="mod.id" :class="{ active: activeModuleId === mod.id }" @click="activeModuleId = mod.id">
              <i class="mdi mdi-layers-outline mb-1"></i><span>{{ mod.nome }}</span>
            </button>
          </div>
        </div>

        <!-- CONTENUTO DEL MODULO ATTIVO -->
        <div class="cw-card" v-if="activeModule">
          <div class="cw-section-title">
            <span class="cw-step-num">1</span>Tipologie Asset e Interventi del modulo: {{ activeModule.nome }}
          </div>

          <div v-if="loadingModuleData" class="text-center py-4">
            <span class="spinner-border spinner-border-sm text-primary mr-2" role="status"></span>
            Caricamento dati del modulo...
          </div>

          <div v-else-if="assetTypes.length === 0" class="text-center py-4 text-muted">
            <i class="mdi mdi-information-outline mr-1"></i> Nessuna tipologia di asset configurata per questo modulo.
          </div>

          <div class="accordion cw-accordion" id="assetTypesAccordion" v-else>
            <div class="card cw-accordion-card mb-3" v-for="(type, index) in assetTypes" :key="type.id">
              <div class="card-header d-flex justify-content-between align-items-center" :id="'heading'+index">
                <h2 class="mb-0 w-100 d-flex justify-content-between align-items-center">
                  <button class="btn btn-link btn-block text-left text-dark font-weight-bold text-decoration-none d-flex align-items-center" type="button" @click="activeAccordionIndex = activeAccordionIndex === index ? null : index">
                    <span>
                      <i class="mdi mdi-cogs mr-2 text-primary"></i>
                      {{ type.nome }}
                      <span class="badge badge-light border ml-2 text-muted" style="font-size:0.75rem;">{{ type.interventi.length }} interventi</span>
                    </span>
                    <i class="mdi text-muted" :class="activeAccordionIndex === index ? 'mdi-chevron-up' : 'mdi-chevron-down'"></i>
                  </button>
                  
                  <!-- Toggle Attivazione Asset Type -->
                  <div @click.stop>
                    <label class="au-checkbox-label mb-0" :for="'switch-asset-'+type.id">
                      <input type="checkbox" class="au-checkbox-input" :id="'switch-asset-'+type.id" v-model="type.is_active" @change="toggleAssetType(type)">
                      <span class="au-checkbox-custom"></span>
                      <span v-if="type.is_active" class="text-primary small font-weight-bold">Attivo</span>
                      <span v-else class="text-muted small font-weight-bold">Disattivo</span>
                    </label>
                  </div>
                </h2>
              </div>

              <div :id="'collapse'+index" class="collapse" :class="{'show': activeAccordionIndex === index}">
                <div class="card-body bg-light rounded-bottom">
                  <h6 class="font-weight-bold text-muted text-uppercase mb-3" style="font-size:0.75rem; letter-spacing: 0.05em;">Interventi Ammessi</h6>
                  
                  <div v-if="type.interventi.length === 0" class="text-muted small">
                    Nessun intervento configurato per questa tipologia.
                  </div>

                  <ul class="list-group list-group-flush border rounded overflow-hidden" v-else>
                    <li class="list-group-item d-flex justify-content-between align-items-center py-3" v-for="int in type.interventi" :key="int.id">
                      <div class="d-flex align-items-start text-left">
                        <i class="mdi mdi-hammer-wrench text-secondary mr-2 mt-1"></i>
                        <div>
                          <span class="font-weight-semibold d-block">{{ int.nome }}</span>
                          <span class="d-block text-muted small" v-if="int.codice">Codice: {{ int.codice }}</span>
                        </div>
                      </div>
                      <div>
                        <label class="au-checkbox-label mb-0" :for="'switch-int-'+type.id+'-'+int.id">
                          <input type="checkbox" class="au-checkbox-input" :id="'switch-int-'+type.id+'-'+int.id" v-model="int.is_active" @change="toggleIntervention(int)">
                          <span class="au-checkbox-custom"></span>
                          <span v-if="int.is_active" class="text-primary small font-weight-bold">Attivo</span>
                          <span v-else class="text-muted small font-weight-bold">Disattivo</span>
                        </label>
                      </div>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      </div> <!-- chiude activeTab config -->
      <!-- TAB 2: GESTIONE ASSET FISICI -->
      <div v-else-if="activeTab === 'assets'">
        <CompanyAssetsManager :companyId="myCompanyId" />
      </div>

    </div>
  </div>
</template>

<script>
import { Navbar } from "@/components";
import CompanyAssetsManager from "@/components/CompanyAssetsManager.vue";

export default {
  name: "AdminModules",
  components: { Navbar, CompanyAssetsManager },
  data() {
    return {
      activeTab: 'assets',
      tipologia: localStorage.getItem("tipologia"),
      myCompanyId: parseInt(localStorage.getItem("fk_company")) || null,
      loadingInit: true,
      loadingModuleData: false,
      modules: [],
      activeModuleId: null,
      assetTypes: [],
      activeAccordionIndex: 0
    };
  },
  computed: {
    activeModule() {
      return this.modules.find(m => m.id === this.activeModuleId) || null;
    }
  },
  watch: {
    activeModuleId(newVal) {
      if (newVal) {
        this.fetchModuleData(newVal);
      }
    }
  },
  mounted() {
    if (this.tipologia !== "B" && this.tipologia !== "A") {
      this.$router.push({ name: "home" });
      return;
    }
    // Selezione tab iniziale via query param (?tab=assets|config|map)
    const requestedTab = this.$route.query.tab;
    if (requestedTab === "config") {
      this.activeTab = "config";
    } else {
      this.activeTab = "assets";
    }
    this.fetchModules();
  },
  methods: {
    fetchModules() {
      if (!this.myCompanyId) return;
      this.axios.get(`/companies/${this.myCompanyId}/modules`).then((response) => {
        this.modules = Array.isArray(response.data) ? response.data : [];
        if (this.modules.length > 0) {
          this.activeModuleId = this.modules[0].id;
        }
      }).catch(console.error).finally(() => {
        this.loadingInit = false;
      });
    },

    fetchModuleData(moduleId) {
      this.loadingModuleData = true;
      this.activeAccordionIndex = 0;
      this.axios.get(`/companies/${this.myCompanyId}/admin-modules/${moduleId}/asset-types`)
        .then(res => {
          if (res.data && res.data.success) {
            this.assetTypes = res.data.data;
          } else {
            this.assetTypes = [];
          }
        })
        .catch(console.error)
        .finally(() => {
          this.loadingModuleData = false;
        });
    },

    toggleAssetType(type) {
      this.axios.post(`/companies/${this.myCompanyId}/admin-modules/asset-types/${type.id}/toggle`, {
        is_active: type.is_active
      }).then(res => {
        if (!res.data.success) {
          throw new Error("Errore backend");
        }
      }).catch(err => {
        console.error(err);
        type.is_active = !type.is_active; // Revert
        this.$swal.fire("Errore", "Impossibile aggiornare lo stato della tipologia", "error");
      });
    },

    toggleIntervention(int) {
      this.axios.post(`/companies/${this.myCompanyId}/admin-modules/interventi/${int.id}/toggle`, {
        is_active: int.is_active
      }).then(res => {
        if (!res.data.success) {
          throw new Error("Errore backend");
        }
      }).catch(err => {
        console.error(err);
        int.is_active = !int.is_active; // Revert
        this.$swal.fire("Errore", "Impossibile aggiornare lo stato dell'intervento", "error");
      });
    }
  }
};
</script>

<style scoped>
.cw-section-title {
  font-size: 0.82rem; font-weight: 700;
  text-transform: uppercase; letter-spacing: 0.08em;
  color: #64748b; margin-bottom: 22px;
  display: flex; align-items: center;
  border-bottom: 1px solid #f1f5f9;
  padding-bottom: 12px;
}
.cw-step-num {
  width: 22px; height: 22px; border-radius: 50%;
  background: linear-gradient(135deg, #F7931E, #E07B00);
  color: #fff; font-size: 0.7rem; font-weight: 800;
  display: inline-flex; align-items: center; justify-content: center;
  margin-right: 10px; flex-shrink: 0;
}

.cw-header-icon {
  width: 48px; height: 48px; border-radius: 14px;
  background: linear-gradient(135deg, #F7931E, #E07B00);
  display: flex; align-items: center; justify-content: center;
  box-shadow: 0 4px 12px rgba(247,147,30,0.35); flex-shrink: 0;
}
.cw-header-icon .mdi { color: #fff; font-size: 1.5rem; }

.cw-card {
  background: #fff; border-radius: 16px;
  box-shadow: 0 2px 16px rgba(0,0,0,0.07);
  padding: 28px; border: 1px solid rgba(0,0,0,0.05);
}

.cw-location-tabs {
  display: flex; background: #f1f5f9;
  border-radius: 12px; padding: 4px; gap: 2px;
}
.cw-loc-tab {
  flex: 1; display: flex; flex-direction: column;
  align-items: center; justify-content: center; gap: 3px;
  padding: 10px 6px; border: none; border-radius: 9px;
  background: transparent; color: #94a3b8;
  font-size: 0.7rem; font-weight: 700;
  text-transform: uppercase; letter-spacing: 0.04em;
  cursor: pointer; transition: all 0.2s;
}
.cw-loc-tab .mdi { font-size: 1.15rem; }
.cw-loc-tab:hover { background: rgba(255,255,255,0.7); color: #F7931E; }
.cw-loc-tab.active {
  background: #fff; color: #E07B00;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
}

.au-banner-info {
  display: flex; align-items: center; text-align: left;
  background-color: #FEF1E0; border-radius: 12px;
  padding: 18px 24px; margin-bottom: 24px;
  border-left: 4px solid #E07B00;
}
.au-banner-icon {
  font-size: 1.8rem; color: #E07B00;
  margin-right: 18px; flex-shrink: 0;
}
.au-banner-title {
  display: block; margin-bottom: 4px;
  color: #1A2238; font-size: 1.05rem; font-weight: 700;
}
.au-banner-text {
  display: block; line-height: 1.5;
  color: #334155; font-size: 0.88rem; font-weight: 500;
}

.cw-accordion-card {
  border: 1px solid #e2e8f0;
  border-radius: 12px !important;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0,0,0,0.02);
}
.cw-accordion-card .card-header {
  background: #fff;
  border-bottom: none;
  padding: 12px 20px;
}
.cw-accordion-card .btn-link {
  color: #1e293b;
}
.cw-accordion-card .btn-link:hover, .cw-accordion-card .btn-link:focus {
  text-decoration: none;
}
.au-checkbox-label {
  display: flex; align-items: center; gap: 10px;
  font-size: 0.88rem; font-weight: 600; color: #334155;
  cursor: pointer; padding: 6px 14px;
  border: 1.5px solid #e2e8f0; border-radius: 10px;
  background: #f8fafc; transition: all 0.15s; user-select: none;
}
.au-checkbox-label:hover { border-color: #F5B871; background: #FEF1E0; }
.au-checkbox-input {
  position: absolute; opacity: 0; width: 0; height: 0; pointer-events: none;
}
.au-checkbox-custom {
  width: 18px; height: 18px; border-radius: 5px;
  border: 2px solid #cbd5e1; background: #fff;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0; transition: all 0.15s;
}
.au-checkbox-input:checked + .au-checkbox-custom {
  background: linear-gradient(135deg, #F7931E, #E07B00);
  border-color: #F7931E;
}
.au-checkbox-input:checked + .au-checkbox-custom::after {
  content: '✓'; color: #fff; font-size: 11px; font-weight: 900; line-height: 1;
}
</style>
